import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { BaseTooltipPlacement } from '@cbhq/cds-common/types';
import { IconButton } from '@cbhq/cds-web/buttons/IconButton';
import { SelectOption } from '@cbhq/cds-web/controls/SelectOption';
import { Dropdown } from '@cbhq/cds-web/dropdown/Dropdown';
import { Box } from '@cbhq/cds-web/layout/Box';
import { HStack } from '@cbhq/cds-web/layout/HStack';
import { VStack } from '@cbhq/cds-web/layout/VStack';
import { Avatar } from '@cbhq/cds-web/media/Avatar';
import { Tooltip } from '@cbhq/cds-web/overlays/tooltip/Tooltip';
import { TableCell, TableRow } from '@cbhq/cds-web/tables';
import { Tag } from '@cbhq/cds-web/tag/Tag';
import { Text } from '@cbhq/cds-web/typography/Text';
import { ActionType, ComponentType, logEvent, markStep } from '@cbhq/client-analytics';

import { CountOrSingleCell } from '../../../components/CountOrSingleCell';
import type { Column } from '../types';
import { useRoles } from '../utils/useRoles';

import { getProductDefinition, getProductDisplayName } from './InviteTray/productCatalog';
import { ProductIcon } from './InviteTray/ProductIcon';

const messages = defineMessages({
  you: {
    id: 'userCell.you',
    defaultMessage: 'You',
    description: 'Label shown in place of the email for the current user',
  },
  roles: {
    // TODO: Use ICU plural syntax ({count, plural, one {# role} other {# roles}}) to
    // support locales with non-trivial plural rules (Russian, Arabic, Polish, etc.).
    id: 'userCell.roles',
    defaultMessage: '{count} roles',
    description: 'Tooltip trigger label showing how many roles a user has for a product',
  },
  entities: {
    // TODO: Use ICU plural syntax ({count, plural, one {# entity} other {# entities}}) to
    // support locales with non-trivial plural rules (Russian, Arabic, Polish, etc.).
    id: 'userCell.entities',
    defaultMessage: '{count} entities',
    description: 'Tooltip trigger label showing how many entities a user belongs to',
  },
  cancelInvite: {
    id: 'userCell.cancelInvite',
    defaultMessage: 'Cancel invite',
    description: 'Dropdown option to cancel a pending invite for a user',
  },
  editRole: {
    id: 'userCell.editRole',
    defaultMessage: 'Edit membership',
    description: 'Dropdown option to edit a user membership',
  },
  removeFromOrg: {
    id: 'userCell.removeFromOrg',
    defaultMessage: 'Remove from {orgName}',
    description: 'Dropdown option to remove a user from the organization',
  },
  moreActions: {
    id: 'userCell.moreActions',
    defaultMessage: 'More actions',
    description: 'Accessibility label for the actions dropdown button in a user row',
  },
  statusActive: {
    id: 'userCell.statusActive',
    defaultMessage: 'Active',
    description: 'Status label for an active team member',
  },
  statusInvitePending: {
    id: 'userCell.statusInvitePending',
    defaultMessage: 'Invite pending',
    description: 'Status label for a team member with a pending invite',
  },
  never: {
    id: 'userCell.never',
    defaultMessage: 'Never',
    description: 'Shown in the Last Online column when the user has never been online',
  },
});

const DROPDOWN_VALUES = ['remove-from-org', 'edit-role', 'cancel-invite'] as const;
type DropdownValue = (typeof DROPDOWN_VALUES)[number];

function isDropdownValue(v: string): v is DropdownValue {
  return (DROPDOWN_VALUES as readonly string[]).includes(v);
}

const statusToColor = {
  active: 'green',
  pending: 'yellow',
} as const;

const statusToMessage = {
  active: messages.statusActive,
  pending: messages.statusInvitePending,
} as const;

const truncateStyle: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const pressableInnerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  width: '100%',
};

const firstTableCellWhitespaceStyle = { paddingY: 0.5, paddingX: 0, paddingStart: 2 } as const;
const tableCellWhitespaceStyle = { paddingY: 0.5 } as const;
const lastTableCellWhitespaceStyle = { paddingY: 0.5, paddingEnd: 3, paddingStart: 2 } as const;

export type AvatarColor = 'blue' | 'teal' | 'purple' | 'pink' | 'green' | 'orange';

export type UserCellMember = {
  userUuid?: string;
  name: string;
  email: string;
  isCurrentUser?: boolean;
  avatarColor: AvatarColor;
  avatarSrc?: string;
  roles: string[];
  status: 'active' | 'pending';
  lastOnline?: Date;
  entities?: string[];
  pendingInvites?: { userUuid?: string; legalEntityId?: string }[];
};

export type UserCellProps = {
  member: UserCellMember;
  orgName: string;
  columns: Column[];
  productName?: string;
  isRemoving?: boolean;
  onNameClick: (member: UserCellMember) => void;
  onRemoveFromOrg: (member: UserCellMember) => void;
  onCancelInvite?: (member: UserCellMember) => void;
  onEditRole?: (member: UserCellMember, productName: string) => void;
  showActions?: boolean;
  /** Whether the current user has permission to remove members from the org */
  canRemove?: boolean;
  /** Whether the current user has permission to edit member access */
  canEdit?: boolean;
};

export function UserCell({
  member,
  orgName,
  columns,
  productName,
  isRemoving,
  onNameClick,
  onRemoveFromOrg,
  onCancelInvite,
  onEditRole,
  showActions = true,
  canRemove = false,
  canEdit = false,
}: UserCellProps) {
  const { formatMessage, formatDate } = useIntl();
  const { roleMap, hasRoleDefinition } = useRoles();

  const isPending = member.status === 'pending';

  const resolvedRoles = useMemo(
    () =>
      member.roles.filter(hasRoleDefinition).map((roleId) => ({
        label: roleMap[roleId].label,
        productKey: roleMap[roleId].productKey,
      })),
    [member.roles, roleMap, hasRoleDefinition],
  );

  const getProductLabel = useCallback(
    (productKey: string) => getProductDisplayName(productKey, formatMessage),
    [formatMessage],
  );

  const productList = useMemo(
    () =>
      [...new Set(resolvedRoles.map((role) => role.productKey))].flatMap((productKey) => {
        const product = getProductDefinition(productKey);
        if (!product) return [];

        return [
          {
            productKey,
            label: getProductLabel(productKey),
            iconName: product.iconName,
            iconBackground: product.iconBackground,
          },
        ];
      }),
    [getProductLabel, resolvedRoles],
  );

  const rolesForProduct = useMemo(
    () =>
      resolvedRoles
        .filter((role) => getProductLabel(role.productKey) === productName)
        .map((role) => role.label),
    [getProductLabel, productName, resolvedRoles],
  );

  const memberEntities = useMemo(() => member.entities ?? [], [member.entities]);

  const handleRowClick = useCallback(() => {
    if (!isPending) {
      onNameClick(member);
    }
  }, [isPending, onNameClick, member]);
  const handleRowKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !isPending) {
        e.preventDefault();
      }
    },
    [isPending],
  );
  const handleActionCellClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);
  const handleActionCellKeyDown = useCallback((e: React.KeyboardEvent) => {
    e.stopPropagation();
  }, []);

  const rowStyle = useMemo(
    () => ({
      opacity: isRemoving ? 0 : 1,
      transition: 'opacity 150ms ease-in-out',
      cursor: isPending ? 'default' : 'pointer',
    }),
    [isRemoving, isPending],
  );

  // TODO: use relative time formatting, e.g. "Today", "3 days ago", "1 month ago", etc.
  const lastOnlineDisplay =
    member.lastOnline != null
      ? formatDate(member.lastOnline, { dateStyle: 'medium' })
      : formatMessage(messages.never);

  const removeFromOrgOption = canRemove ? (
    <SelectOption
      value={'remove-from-org' satisfies DropdownValue}
      title={
        <Text as="span" color="fgNegative">
          {formatMessage(messages.removeFromOrg, { orgName })}
        </Text>
      }
    />
  ) : null;

  const editRoleOption =
    canEdit && onEditRole != null ? (
      <SelectOption
        value={'edit-role' satisfies DropdownValue}
        title={formatMessage(messages.editRole)}
      />
    ) : null;

  const hasActionOptions =
    member.status === 'pending'
      ? canEdit && onCancelInvite != null
      : editRoleOption != null || canRemove;

  let dropdownContent: React.ReactNode;
  if (member.status === 'pending') {
    dropdownContent = (
      <SelectOption
        testID="team-member-cancel-invite-option"
        value={'cancel-invite' satisfies DropdownValue}
        title={
          <Text as="span" color="fgNegative">
            {formatMessage(messages.cancelInvite)}
          </Text>
        }
      />
    );
  } else {
    dropdownContent = (
      <>
        {editRoleOption}
        {removeFromOrgOption}
      </>
    );
  }

  const handleDropdownChange = useCallback(
    (value: string) => {
      if (!isDropdownValue(value)) return;
      if (value === 'cancel-invite') {
        logEvent('member_invite_cancelled_clicked', {
          componentType: ComponentType.dropdown,
          action: ActionType.select,
          entry_point: 'overflow_menu',
        });
        onCancelInvite?.(member);
      } else if (value === 'remove-from-org') {
        if (member.userUuid) {
          markStep('navigate_to_workspace_remove_member_modal');
        }
        onRemoveFromOrg(member);
      } else if (value === 'edit-role') {
        onEditRole?.(member, productName ?? '');
      }
    },
    [member, onCancelInvite, onEditRole, onRemoveFromOrg, productName],
  );

  return (
    <TableRow
      onClick={isPending ? undefined : handleRowClick}
      onKeyDown={isPending ? undefined : handleRowKeyDown}
      style={rowStyle}
      data-testid="team-member-row"
      data-email={member.email}
      data-dd-action-name={isPending ? undefined : 'team_member_row_open'}
    >
      {columns.map((col) => {
        switch (col) {
          case 'name':
            return (
              <TableCell
                outerSpacing={firstTableCellWhitespaceStyle}
                key="name"
                overflow="truncate"
              >
                <HStack alignItems="center" gap={2} style={pressableInnerStyle}>
                  <Avatar
                    size="l"
                    colorScheme={member.avatarColor}
                    name={member.name}
                    src={member.avatarSrc}
                    alt={member.name}
                    shape="circle"
                  />
                  <VStack minWidth={0}>
                    <Text font="label1" style={truncateStyle}>
                      {member.name}
                    </Text>
                    <Text font="label2" color="fgMuted" style={truncateStyle}>
                      {member.isCurrentUser ? formatMessage(messages.you) : member.email}
                    </Text>
                  </VStack>
                </HStack>
              </TableCell>
            );
          case 'entity':
            return (
              <TableCell outerSpacing={tableCellWhitespaceStyle} key="entity" overflow="truncate">
                <Box width="100%" minWidth={0}>
                  {memberEntities.length === 1 ? (
                    <Text as="span" font="label2" style={truncateStyle}>
                      {memberEntities[0]}
                    </Text>
                  ) : (
                    <CountOrSingleCell items={memberEntities} countMessage={messages.entities} />
                  )}
                </Box>
              </TableCell>
            );
          case 'product':
            if (productList.length === 0) {
              return (
                <TableCell outerSpacing={tableCellWhitespaceStyle} key="product">
                  <span />
                </TableCell>
              );
            }

            return (
              <TableCell outerSpacing={tableCellWhitespaceStyle} key="product">
                <HStack alignItems="center" gap={1}>
                  {productList.map((product) => (
                    <Tooltip
                      key={product.productKey}
                      content={product.label}
                      placement={'bottom-start' as BaseTooltipPlacement}
                    >
                      <HStack as="span" accessibilityLabel={product.label}>
                        <ProductIcon name={product.iconName} background={product.iconBackground} />
                      </HStack>
                    </Tooltip>
                  ))}
                </HStack>
              </TableCell>
            );
          case 'role':
            return (
              <TableCell outerSpacing={tableCellWhitespaceStyle} key="role" overflow="truncate">
                <CountOrSingleCell items={rolesForProduct} countMessage={messages.roles} />
              </TableCell>
            );
          case 'status':
            return (
              <TableCell outerSpacing={tableCellWhitespaceStyle} key="status" overflow="truncate">
                <Tag colorScheme={statusToColor[member.status]}>
                  {formatMessage(statusToMessage[member.status])}
                </Tag>
              </TableCell>
            );
          case 'lastOnline':
            return (
              <TableCell
                outerSpacing={tableCellWhitespaceStyle}
                key="lastOnline"
                overflow="truncate"
              >
                <Text as="p" display="block" font="label2" color="fgMuted" overflow="truncate">
                  {lastOnlineDisplay}
                </Text>
              </TableCell>
            );
          default:
            return col satisfies never;
        }
      })}

      {showActions && (
        <TableCell outerSpacing={lastTableCellWhitespaceStyle} width={48} justifyContent="center">
          {!member.isCurrentUser && hasActionOptions && (
            <Box onClick={handleActionCellClick} onKeyDown={handleActionCellKeyDown}>
              <Dropdown content={dropdownContent} onChange={handleDropdownChange}>
                <IconButton
                  name="more"
                  variant="secondary"
                  transparent
                  accessibilityLabel={formatMessage(messages.moreActions)}
                  data-dd-action-name="team_member_overflow_open"
                  data-testid="team-member-actions-button"
                />
              </Dropdown>
            </Box>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}
